import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {StaticImageData} from 'next/image';
import DisplayImage from '@/components/Ui-components/display-image';
import parse from 'html-react-parser';
import {contentMaxWidth, borderRadius, colors, fontWeight, letterSpacing} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

export interface Web3ServiceItem {
    /** Leading visual. Omit when `step` is used — the badge then shows the step number. */
    icon?: StaticImageData;
    /** Step number shown in the leading badge (e.g. "01") instead of an icon. */
    step?: string;
    /** Small uppercase label above the title (e.g. "ASSESS"). */
    label?: string;
    title: string;
    subtitle?: string;
    description: string;
    tags: string[];
}

export interface Web3EndToEndServicesProps {
    heading: string;
    subheading: string;
    services: Web3ServiceItem[];
    chevronUp: StaticImageData;
    chevronDown: StaticImageData;
    /** Optional kicker above the section heading. */
    eyebrow?: string;
}

const sectionHeadingSx = {
    textAlign: 'center',
    mb: 5
} as const;

const headingTypoSx = {
    fontSize: {xs: '32px', md: '46px'},
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: {xs: '42px', md: '62px'},
    mb: 2
} as const;

const subheadingTypoSx = {
    fontSize: '16px',
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: '29px',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const accordionListSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px'
} as const;

const iconBgSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.lg,
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const tagSx = {
    bgcolor: colors.background.tag,
    borderRadius: '999px',
    px: '14px',
    py: '7px',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0
} as const;

const stepBadgeSx = {
    ...iconBgSx,
    bgcolor: colors.primary.deep
} as const;

const stepTextSx = {
    fontSize: '18px',
    fontWeight: fontWeight.bold,
    color: colors.text.inverse,
    lineHeight: '18px'
} as const;

const itemLabelSx = {
    fontSize: '13px',
    fontWeight: fontWeight.semibold,
    color: colors.primary.link,
    letterSpacing: letterSpacing.eyebrow,
    textTransform: 'uppercase' as const,
    lineHeight: '18px'
} as const;

const tagTextSx = {
    fontSize: '14px',
    fontWeight: fontWeight.medium,
    color: colors.primary.link,
    lineHeight: '14px',
    whiteSpace: 'nowrap' as const
} as const;

const Web3EndToEndServices = ({heading, subheading, services, chevronUp, chevronDown, eyebrow}: Web3EndToEndServicesProps) => {
    const [openIdx, setOpenIdx] = useState<number>(0);

    return (
        <MUIContainer>
            <Box sx={sectionHeadingSx}>
                {eyebrow && <Eyebrow text={eyebrow} sx={{textAlign: 'center'}} />}
                <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                    {heading}
                </Typography>
                <Typography className="font-regular-text remove-underline" sx={subheadingTypoSx}>
                    {parse(subheading)}
                </Typography>
            </Box>

            <Box sx={accordionListSx}>
                {services.map((service, idx) => {
                    const isOpen = openIdx === idx;
                    return (
                        <Box
                            key={service.title}
                            onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                            sx={{
                                bgcolor: colors.background.default,
                                border: `1px solid ${colors.neutral.borderLight}`,
                                borderRadius: borderRadius.xl,
                                px: '29px',
                                py: '25px',
                                cursor: 'pointer',
                                boxShadow: isOpen ? '0px 10px 14px rgba(16,24,40,0.08)' : '0px 2px 5px rgba(16,24,40,0.04)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '24px'
                            }}
                        >
                            {/* Header row */}
                            <Box sx={{display: 'flex', gap: '20px', alignItems: 'flex-start'}}>
                                {service.step ? (
                                    <Box sx={stepBadgeSx}>
                                        <Typography className="font-headers" sx={stepTextSx}>
                                            {service.step}
                                        </Typography>
                                    </Box>
                                ) : (
                                    service.icon && (
                                        <Box sx={iconBgSx}>
                                            <Box sx={{width: 24, height: 24, position: 'relative'}}>
                                                <DisplayImage src={service.icon} alt="" fill />
                                            </Box>
                                        </Box>
                                    )
                                )}

                                <Box sx={{flex: '1 0 0', minWidth: 0, pt: '2px', display: 'flex', flexDirection: 'column', gap: '5px'}}>
                                    {service.label && (
                                        <Typography className="font-headers" sx={itemLabelSx}>
                                            {service.label}
                                        </Typography>
                                    )}
                                    <Typography
                                        component="h3"
                                        className="font-headers"
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: fontWeight.semibold,
                                            color: colors.text.primary,
                                            lineHeight: '27px'
                                        }}
                                    >
                                        {service.title}
                                    </Typography>
                                    {service.subtitle && (
                                        <Typography
                                            className="font-regular-text remove-underline"
                                            sx={{
                                                fontSize: '15px',
                                                fontWeight: fontWeight.regular,
                                                color: isOpen ? colors.text.primary : colors.text.secondary,
                                                lineHeight: '22.5px'
                                            }}
                                        >
                                            {service.subtitle}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{pt: '10px', flexShrink: 0, width: 24, height: 24, position: 'relative'}}>
                                    <DisplayImage src={isOpen ? chevronUp : chevronDown} alt="" fill />
                                </Box>
                            </Box>

                            {/* Expanded content */}
                            {isOpen && (
                                <Box
                                    sx={{
                                        borderTop: `1px solid ${colors.neutral.borderLight}`,
                                        pt: '25px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '24px'
                                    }}
                                >
                                    <Typography
                                        className="font-regular-text remove-underline"
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: fontWeight.regular,
                                            color: colors.text.primary,
                                            lineHeight: '28px'
                                        }}
                                    >
                                        {parse(service.description)}
                                    </Typography>
                                    {service.tags.length > 0 && (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                                            {service.tags.map((tag) => (
                                                <Box key={tag} sx={tagSx}>
                                                    <Typography sx={tagTextSx}>{tag}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </MUIContainer>
    );
};

export default memo(Web3EndToEndServices);
